import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import { useSEO } from "../lib/seo";
import {
  Wrench, Lightning, Broom, Hammer, Truck, Tree, PaintBrush, Toolbox,
  Ruler, ThermometerSimple, Books, PawPrint, ArrowRight, MapPin, ShieldCheck, MagnifyingGlass,
} from "@phosphor-icons/react";

const ICONS = { Wrench, Lightning, Broom, Hammer, Truck, Tree, PaintBrush, Toolbox, Ruler, ThermometerSimple, Books, PawPrint };

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useSEO({
    title: "Hire Skilled Workers, Freelancers & Virtual Staff in Canada",
    description: "Canada's hiring platform for temp jobs, skilled trades, freelancers, and remote virtual staff. Small businesses and homeowners connect with vetted talent — local or worldwide.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CanHireSolution.com",
      url: "https://canhiresolution.com",
      description: "Canadian hiring platform for temp jobs, skilled trades, freelancers, and virtual staff.",
      areaServed: { "@type": "Country", name: "Canada" },
      logo: "https://canhiresolution.com/canhiresolution-logo.png",
    },
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/workers?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-8"
          >
            <div className="inline-flex items-center gap-2 border border-slate-200 bg-white px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-6">
              <span className="w-1.5 h-1.5 bg-accent-red" /> Canada&apos;s hiring platform — local &amp; remote
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-slate-900 mb-6">
              Hire skilled<br/>
              Canadians.<br/>
              <span className="text-brand">Get it done.</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mb-8 leading-relaxed">
              Temp jobs, skilled trades, freelancers, and virtual staff — all in one place.
              For homeowners, small businesses, and busy founders.
            </p>

            <form onSubmit={onSearch} className="bg-white border-2 border-slate-900 p-2 flex flex-col sm:flex-row gap-2 max-w-xl">
              <div className="flex items-center gap-2 px-3 flex-1">
                <MagnifyingGlass size={20} className="text-slate-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="What do you need done?"
                  className="w-full py-3 text-base outline-none"
                  data-testid="hero-search-input"
                />
              </div>
              <button type="submit" className="bg-brand text-white px-6 py-3 font-semibold flex items-center justify-center gap-2 hover:bg-[#122148]" data-testid="hero-search-btn">
                Search <ArrowRight size={18} weight="bold" />
              </button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-brand" /> Verified pros</div>
              <div className="flex items-center gap-2"><MapPin size={18} className="text-accent-red" /> All 10 provinces + remote</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-4 self-end"
          >
            <div className="relative">
              <img
                src="/canhiresolution-logo.png"
                alt="CanHireSolution.com brand"
                className="w-full max-w-md mx-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-t border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Browse Categories</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Pick a service.</h2>
            </div>
            <Link to="/workers" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-brand">
              All services <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories.map((c, i) => {
              const Ico = ICONS[c.icon] || Wrench;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={`/workers?category=${c.id}`}
                    className="block border border-slate-200 p-5 bg-white hover:-translate-y-1 hover:shadow-md transition-all group"
                    data-testid={`category-${c.id}`}
                  >
                    <Ico size={28} className="text-brand mb-3 group-hover:scale-110 transition-transform" />
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-slate-200 py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-12">Three steps. Done.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
            {[
              { n: "01", t: "Post your job", d: "Describe what you need in 60 seconds. Free to post." },
              { n: "02", t: "Get matched", d: "Receive proposals from verified Canadian pros within minutes." },
              { n: "03", t: "Hire & review", d: "Chat, negotiate, hire, and leave a review when done." },
            ].map((s) => (
              <div key={s.n} className="bg-white p-8">
                <div className="font-mono text-sm text-accent-red mb-4">{s.n}</div>
                <h3 className="font-display text-2xl mb-2">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="border-t border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Stay in the loop</div>
            <h2 className="font-display text-3xl tracking-tight">Hiring tips. Pro stories. Canadian news.</h2>
          </div>
          <div className="md:col-span-7">
            <NewsletterSignup source="landing" />
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">For skilled pros</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Grow your business.<br/>Keep more of what you earn.</h2>
            <p className="text-slate-300 max-w-lg leading-relaxed">
              Get qualified leads from customers in your area. No hidden fees. Cancel anytime.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col sm:flex-row md:justify-end gap-3">
            <Link to="/pricing" className="bg-accent-red text-white px-8 py-4 font-semibold text-center hover:bg-[#D55915]" data-testid="cta-pricing">
              View pricing
            </Link>
            <Link to="/login" className="bg-white text-slate-900 px-8 py-4 font-semibold text-center hover:bg-slate-100" data-testid="cta-join-pro">
              Join as a Pro
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
